import { cva, type VariantProps } from "class-variance-authority"
import { motion } from "framer-motion"
import { formatDistanceToNowLang, parseEmailSource, SOURCE_AMPHORA } from "lopermut"
import {
  AlertTriangle,
  Award,
  Ban,
  CheckCheck,
  ChevronRight,
  Code2,
  ExternalLink,
  FileText,
  Headset,
  Loader2,
  MessageCircleQuestion,
  Sparkles,
  Terminal,
  Trash2,
} from "lucide-react"
import React, { useMemo, useState } from "react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../atoms/Collapsible"
import { Popover, PopoverContent, PopoverTrigger } from "../../atoms/Popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../atoms/Tooltip"
import { FlagsBadge } from "../../molecules/FlagsBadge"
import { cn } from "../../utils"
import { AudioPlayer } from "./AudioPlayer"
import { ColorConfig } from "./Chat"
import { FilePreview } from "./FilePreview"
import { MarkdownRenderer } from "./MarkdownRenderer"

const chatBubbleVariants = cva(
  "group/message flex flex-col relative break-words rounded-xl p-3 px-4 gap-2 text-sm sm:max-w-[70%] max-w-[90%] ring-0 focus:ring transition duration-200",
  {
    variants: {
      isUser: {
        true: "bg-chart-primary text-primary-foreground dark:text-foreground rounded-br-none data-[fake=true]:bg-muted data-[fake=true]:text-foreground focus:ring-chart-primary",
        false:
          "bg-muted text-foreground min-w-[100px] rounded-bl-none data-[fake=true]:bg-testing-1 data-[fake=true]:text-primary-foreground data-[fake=true]:dark:text-foreground z-10 focus:ring-muted-foreground",
      },
      isAuthor: {
        true: "bg-chart-secondary/30",
        false: "",
      },
      animation: {
        none: "",
        slide: "duration-300 animate-in fade-in-0",
        scale: "duration-300 animate-in fade-in-0 zoom-in-75",
        fade: "duration-500 animate-in fade-in-0",
      },
    },
    compoundVariants: [
      {
        isUser: true,
        animation: "slide",
        class: "slide-in-from-right",
      },
      {
        isUser: false,
        animation: "slide",
        class: "slide-in-from-left",
      },
      {
        isUser: true,
        animation: "scale",
        class: "origin-bottom-right",
      },
      {
        isUser: false,
        animation: "scale",
        class: "origin-bottom-left",
      },
    ],
  },
)

export type Animation = VariantProps<typeof chatBubbleVariants>["animation"]

interface Attachment {
  name?: string
  contentType?: string
  url: string
}

interface PartialToolCall {
  state: "partial-call"
  toolName: string
}

interface ToolCall {
  state: "call"
  toolName: string
}

interface ToolResult {
  state: "result"
  toolName: string
  result: {
    __cancelled?: boolean
    [key: string]: unknown
  }
}

type ToolInvocation = PartialToolCall | ToolCall | ToolResult

interface ReasoningPart {
  type: "reasoning"
  reasoning: string
}

interface ToolInvocationPart {
  type: "tool-invocation"
  toolInvocation: ToolInvocation
}

interface TextPart {
  type: "text"
  text: string
}

// For compatibility with AI SDK types, not used
interface SourcePart {
  type: "source"
}

type MessagePart = TextPart | ReasoningPart | ToolInvocationPart | SourcePart

type Ticket = {
  id: string
} & (
  | {
      kind: "ticket"
    }
  | {
      kind: "question"
      resolvedAutomatically?: boolean
      subject?: string | null
      summary?: string
    }
)

type Opportunity = {
  id: string
  summary?: string | null
  qualification?: "Detected" | "Potential" | "Qualified" | "Disqualified"
}

type CapabilityExecution = {
  id: string
  capabilityId: string
  capabilityIdentifier: string
  capabilityName: string
  capabilityKind: "Native" | "Integration" | "Webhook" | "Instruction" | "MCP"
  result: unknown
  reason: string | null
  status: "Success" | "Unsuccess" | "Cached" | "Error" | "Skipped"
  evaluation: unknown
  createdAt: string
  cacheExecutionId: string | null
  isVisibleInConversation: boolean
}

type CustomerLog = {
  id: string
  kind:
    "AIDisabled" | "AIResumed" | "BrandRelatedFraudReported" | "FraudDetected" | "TrollingDetected"
  source: string | null
  employee: {
    id: string
    name: string
  } | null
  admin: {
    id: string
    name: string
  } | null
  createdAt: string
}

export const MessageKindEnum = {
  Generic: "Generic",
  Campaign: "Campaign",
  Reengage: "Reengage",
  Question: "Question",
  Action: "Action",
  Closing: "Closing",
  OptOut: "OptOut",
  Employee: "Employee",
  Resolver: "Resolver",
  FindOrder: "FindOrder",
  CancelOrder: "CancelOrder",
  CheckReturn: "CheckReturn",
} as const

export interface Message {
  id: string
  role: "user" | "assistant" | string
  format: "text" | "audio" | "image" | "video" | "document"
  kind?: keyof typeof MessageKindEnum | null
  content: string | null
  mediaUrl?: string | null
  transcription?: string | null
  externalDeletedAt?: string | Date | null
  createdAt?: Date
  experimental_attachments?: Attachment[]
  toolInvocations?: ToolInvocation[]
  parts?: MessagePart[]
  readAt: Date | null
  messageReaction: string | null
  tickets?: Ticket[]
  opportunities?: Opportunity[]
  capabilityExecutions?: CapabilityExecution[]
  customerLogs?: CustomerLog[]
  authorName?: string | null
  source?: string | null
  whatsappTemplate?: {
    id: string
    name: string
    mediaFormat?: "IMAGE" | "VIDEO" | "GIF" | "DOCUMENT" | "AUDIO" | null
    mediaUrl?: string | null
  } | null
  sentAt?: string | Date | null
  isOneTimeTemplate?: boolean
  whatsappTemplateId?: string | null
  whatsappTemplateButtons?: { text: string; url: string | null }[]
  responseToMessageId?: string | null
  failedAt?: string | Date | null
  failedReason?: string | null
  renderAttachments?: () => React.ReactNode
  renderTimelineMarkers?: () => React.ReactNode
  isGenerative?: boolean | null
  flagCounts?: { issues: number; frictions: number }
}

export interface ChatMessageProps extends Message {
  showTimeStamp?: boolean
  animation?: Animation
  actions?: React.ReactNode
  onClick?: () => void
  supportCopy?: {
    questionCreated: string
    questionSolvedAutomatically: string
    ticketCreated: string
    questionSubjectLabel?: string
    questionSummaryLabel?: string
  }
  opportunityCopy?: string
  renderCapabilityExecutions?: (executions: CapabilityExecution[]) => React.ReactNode
  renderCustomerLogMarkers?: (logs: CustomerLog[]) => React.ReactNode
  colorConfig?: ColorConfig
  publicMode?: boolean
  isFakeCustomer?: boolean
  messages: Message[]
  replyMessageCopy?: (kind: Message["kind"]) => string
  messageNotGeneratedByPermutCopy: string
  messagePendingCopy?: string
  messagePendingTooltipCopy?: string
  messageIsWhatsappTemplateCopy: string
  messageIsAmphoraCopy: string
  messageExternalSenderCopy: string
  messageExternalDeletedCopy: string
  suppressLinks?: boolean
  hiddenLinkCopy?: string
  locale?: string
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  role,
  content,
  mediaUrl,
  format,
  transcription,
  externalDeletedAt,
  createdAt,
  showTimeStamp = false,
  animation = "none",
  actions,
  experimental_attachments,
  toolInvocations,
  parts,
  readAt,
  messageReaction,
  supportCopy,
  tickets,
  opportunities,
  opportunityCopy,
  capabilityExecutions,
  customerLogs,
  renderCapabilityExecutions,
  renderCustomerLogMarkers,
  colorConfig,
  publicMode = false,
  isFakeCustomer = false,
  authorName,
  source,
  whatsappTemplate,
  messages,
  responseToMessageId,
  replyMessageCopy,
  id,
  failedAt,
  failedReason,
  renderAttachments,
  renderTimelineMarkers,
  isGenerative,
  flagCounts,
  messageNotGeneratedByPermutCopy,
  messagePendingCopy,
  messagePendingTooltipCopy,
  messageIsWhatsappTemplateCopy,
  messageIsAmphoraCopy,
  messageExternalSenderCopy,
  messageExternalDeletedCopy,
  sentAt,
  isOneTimeTemplate,
  whatsappTemplateId,
  whatsappTemplateButtons = [],
  suppressLinks,
  hiddenLinkCopy,
  locale,
  onClick,
}) => {
  const files = useMemo(() => {
    return experimental_attachments?.map((attachment) => {
      const dataArray = dataUrlToUint8Array(attachment.url)
      const file = new File([dataArray], attachment.name ?? "Unknown")
      return file
    })
  }, [experimental_attachments])

  const isUser = role === "user"
  const hasWhatsappTemplate = !!whatsappTemplateId
  const isAmphoraMessage = source === SOURCE_AMPHORA
  const externalSenderEmail = parseEmailSource(source)
  const externalSenderText = externalSenderEmail
    ? messageExternalSenderCopy.replace("{{email}}", externalSenderEmail)
    : null
  const isExternalAssistantMessage = !!authorName || !isGenerative || isAmphoraMessage
  const onlyInternal = !publicMode
  const [isTranscriptionExpanded, setIsTranscriptionExpanded] = useState(false)
  const [isDeletedMessageExpanded, setIsDeletedMessageExpanded] = useState(false)
  // Independent, composable top badges: "made outside Permut" (assistant that the AI didn't
  // generate) and "external sender" (replied from another address) can appear alone or together.
  const topBadges: Array<{ text: string; tooltip?: string }> = (() => {
    if (hasWhatsappTemplate) {
      const isPending = isOneTimeTemplate && !sentAt && !failedAt && !externalDeletedAt
      return [
        { text: [messageIsWhatsappTemplateCopy, authorName].filter(Boolean).join(" - ") },
        ...(isPending && messagePendingCopy
          ? [{ text: messagePendingCopy, tooltip: messagePendingTooltipCopy }]
          : []),
      ]
    }
    if (authorName) return [{ text: authorName }]
    if (isAmphoraMessage) return [{ text: messageIsAmphoraCopy }]

    const badges: string[] = []
    if (!isUser && !isGenerative) badges.push(messageNotGeneratedByPermutCopy)
    if (externalSenderText) badges.push(externalSenderText)
    return badges.map((text) => ({ text }))
  })()

  // Format time: show only time if today, otherwise show date and time
  let formattedTime = ""
  if (createdAt) {
    const now = new Date()
    const isToday =
      createdAt.getFullYear() === now.getFullYear() &&
      createdAt.getMonth() === now.getMonth() &&
      createdAt.getDate() === now.getDate()
    if (isToday) {
      formattedTime = createdAt.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      })
    } else {
      formattedTime = `${createdAt.toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
      })} ${createdAt.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      })}`
    }
  }

  function renderMedia() {
    if (!mediaUrl) return null

    if (format === "audio") {
      return (
        <div className="sm:min-w-[340px] min-w-[240px] w-full h-10 relative">
          <AudioPlayer src={mediaUrl} isUser={isUser} />
        </div>
      )
    }

    if (format === "image") {
      return (
        <img
          src={mediaUrl}
          className="h-auto w-auto max-h-[460px] max-w-[460px] rounded-lg object-scale-down"
        />
      )
    }

    if (format === "video") {
      return <video src={mediaUrl} controls />
    }

    if (format === "document") {
      const fileName = mediaUrl.split("/").pop()?.split("?")[0] ?? "document"
      const isLiveUserMessage = isUser && !isFakeCustomer
      return (
        <a
          href={mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors max-w-[280px]",
            isLiveUserMessage
              ? "border-white/30 bg-white/20 text-primary-foreground hover:bg-white/30"
              : "border-muted-foreground/30 bg-muted text-foreground hover:bg-muted/80",
          )}
        >
          <FileText
            className={cn(
              "size-5 shrink-0",
              isLiveUserMessage ? "text-primary-foreground/70" : "text-muted-foreground",
            )}
          />
          <span className="truncate flex-1">{decodeURIComponent(fileName)}</span>
          <ExternalLink
            className={cn(
              "size-4 shrink-0",
              isLiveUserMessage ? "text-primary-foreground/70" : "text-muted-foreground",
            )}
          />
        </a>
      )
    }

    return null
  }

  function renderTranscription() {
    if (!transcription) return null

    return (
      <div
        className="group/transcription relative border-l-2 border-muted-foreground px-1"
        onClick={() => isTranscriptionExpanded && setIsTranscriptionExpanded(false)}
      >
        <div
          className={cn(
            "text-xs opacity-60 italic transition-all duration-300",
            !isTranscriptionExpanded && "line-clamp-3 group-hover/transcription:blur-[2px]",
          )}
        >
          {transcription}
        </div>
        {!isTranscriptionExpanded && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/transcription:opacity-100 transition-opacity duration-200">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setIsTranscriptionExpanded(true)
              }}
              className="bg-background/80 backdrop-blur-sm text-xs text-muted-foreground px-2 py-0.5 rounded-full border border-border shadow-sm hover:bg-background transition-colors cursor-pointer"
            >
              Show more
            </button>
          </div>
        )}
      </div>
    )
  }

  function renderWhatsappTemplate() {
    if (!whatsappTemplate || !whatsappTemplate.mediaUrl) return null

    switch (whatsappTemplate.mediaFormat) {
      case "IMAGE":
      case "GIF":
        return (
          <div>
            <img src={whatsappTemplate.mediaUrl} className="h-auto w-full rounded-md" />
          </div>
        )
      default:
        return null
    }
  }

  function renderWhatsappTemplateButtons() {
    if (!whatsappTemplateButtons.length) return null

    const className =
      "-mx-4 flex items-center justify-center gap-1.5 border-t border-current/15 px-4 py-2 text-sm font-medium"

    return (
      <div className="-mb-1 mt-2">
        {whatsappTemplateButtons.map((button, index) => {
          const buttonContent = (
            <>
              <ExternalLink className="size-4" />
              <span>{button.text}</span>
            </>
          )

          if (!button.url || suppressLinks) {
            return (
              <div key={index} className={className}>
                {buttonContent}
              </div>
            )
          }

          return (
            <a
              key={index}
              href={button.url}
              target="_blank"
              rel="noreferrer"
              className={cn(className, "hover:underline")}
            >
              {buttonContent}
            </a>
          )
        })}
      </div>
    )
  }

  function renderReplyToMessage() {
    if (!responseToMessageId) return null

    const replyToMessage = responseToMessageId
      ? messages.find((message) => message.id === responseToMessageId)
      : null

    if (!replyToMessage) return null

    const onScrollToMessage = () => {
      const replyToMessageElement: HTMLDivElement | null = document.querySelector(
        `#message-${replyToMessage.id}`,
      )

      replyToMessageElement?.scrollIntoView({ behavior: "smooth" })
      replyToMessageElement?.focus()
    }

    return (
      <button
        className="border-l-2 block px-1 text-xs opacity-60 italic whitespace-pre-wrap text-left cursor-pointer"
        onClick={onScrollToMessage}
      >
        {replyToMessage.content ?? replyMessageCopy?.(replyToMessage.kind)}
      </button>
    )
  }

  if (externalDeletedAt) {
    const deletedMessagePreview = content ?? transcription ?? messageExternalDeletedCopy

    return (
      <div className={cn("flex flex-col", isUser ? "items-end" : "items-start")}>
        <div
          className={cn(
            chatBubbleVariants({ isUser, isAuthor: isExternalAssistantMessage, animation }),
            "border border-dashed border-muted-foreground/40 bg-muted/50 text-muted-foreground",
            isUser
              ? "!bg-background"
              : "data-[fake=false]:bg-chart-primary/10 data-[fake=true]:bg-testing-1/20 data-[fake=true]:text-foreground",
          )}
          data-fake={isFakeCustomer}
          id={`message-${id}`}
          tabIndex={0}
          role="button"
          aria-expanded={isDeletedMessageExpanded}
          onClick={() => setIsDeletedMessageExpanded((isExpanded) => !isExpanded)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault()
              setIsDeletedMessageExpanded((isExpanded) => !isExpanded)
            }
          }}
        >
          {!isDeletedMessageExpanded ? (
            <span
              className={cn(
                "block max-w-full overflow-hidden text-ellipsis whitespace-nowrap transition-[filter] duration-200",
                "select-none blur-[4px]",
              )}
            >
              {deletedMessagePreview}
            </span>
          ) : (
            <div className="flex flex-col gap-2">
              {files && files.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {files.map((file, index) => (
                    <FilePreview file={file} key={index} />
                  ))}
                </div>
              ) : null}
              {renderMedia()}
              {renderTranscription()}
              {renderWhatsappTemplate()}
              {content ? <MarkdownRenderer>{content}</MarkdownRenderer> : null}
              {renderAttachments && renderAttachments()}
            </div>
          )}
        </div>
        <div
          className={cn(
            "mt-1 flex items-center gap-1 px-1 text-xs text-muted-foreground/70",
            isUser ? "justify-end" : "justify-start",
          )}
        >
          <Trash2 className="size-3.5" />
          <span>
            {messageExternalDeletedCopy}{" "}
            {formatDistanceToNowLang(externalDeletedAt, { addSuffix: true })}
          </span>
        </div>
        {showTimeStamp && createdAt ? (
          <time
            dateTime={createdAt.toISOString()}
            className={cn(
              "mt-1 block px-1 text-xs opacity-50",
              animation !== "none" && "duration-500 animate-in fade-in-0",
            )}
          >
            {formattedTime}
          </time>
        ) : null}
      </div>
    )
  }

  if (isUser) {
    return (
      <div
        className={cn(
          "flex flex-col",
          isUser ? "items-end" : "items-start",
          onClick && "cursor-pointer",
        )}
        onClick={
          onClick
            ? (e) => {
                e.stopPropagation()
                onClick()
              }
            : undefined
        }
      >
        {files ? (
          <div className="mb-1 flex flex-wrap gap-2">
            {files.map((file, index) => {
              return <FilePreview file={file} key={index} />
            })}
          </div>
        ) : null}

        <div
          className={cn(chatBubbleVariants({ isUser, isAuthor: !!authorName, animation }))}
          data-fake={isFakeCustomer}
          style={colorConfig?.user}
          id={`message-${id}`}
          tabIndex={0}
        >
          {renderReplyToMessage()}
          <MessageTopBadges badges={topBadges} />
          {renderMedia()}
          {renderTranscription()}
          {content ? (
            <MarkdownRenderer suppressLinks={suppressLinks} hiddenLinkCopy={hiddenLinkCopy}>
              {content}
            </MarkdownRenderer>
          ) : null}
        </div>
        {messageReaction && (
          <div className="py-2 px-3 text-sm bg-muted rounded-full -translate-y-2 mr-2 border border-background">
            <span>{messageReaction}</span>
          </div>
        )}
        {(showTimeStamp && createdAt) || flagCounts ? (
          <div
            className={cn(
              "mt-1 flex w-full items-center justify-end gap-1 px-1 text-xs",
              animation !== "none" && "duration-500 animate-in fade-in-0",
            )}
          >
            {showTimeStamp && createdAt ? (
              <time dateTime={createdAt.toISOString()} className="opacity-50">
                {formattedTime}
              </time>
            ) : null}
            {flagCounts ? (
              <FlagsBadge issuesCount={flagCounts.issues} frictionsCount={flagCounts.frictions} />
            ) : null}
          </div>
        ) : null}
        {onlyInternal && tickets && tickets.length > 0 && (
          <div className="w-full flex flex-col gap-4 py-2">
            {tickets.map((ticket, ticketIndex) => (
              <SupportMarker
                key={`${ticket.kind}-${ticket.id}-${ticketIndex}`}
                supportCopy={supportCopy}
                ticketId={ticket.id}
                kind={ticket.kind}
                solved={"resolvedAutomatically" in ticket && ticket.resolvedAutomatically}
                questionSubject={ticket.kind === "question" ? ticket.subject : undefined}
                questionSummary={ticket.kind === "question" ? ticket.summary : undefined}
              />
            ))}
          </div>
        )}
        {onlyInternal && opportunities && opportunities.length > 0 && (
          <div className="w-full flex flex-col gap-4 py-2">
            {opportunities.map((opportunity) => (
              <OpportunityMarker
                key={opportunity.id}
                opportunity={opportunity}
                opportunityCopy={opportunityCopy}
              />
            ))}
          </div>
        )}
        {onlyInternal &&
          capabilityExecutions &&
          capabilityExecutions.length > 0 &&
          renderCapabilityExecutions && (
            <div className="w-full flex flex-col gap-4 py-2">
              {renderCapabilityExecutions(capabilityExecutions)}
            </div>
          )}
        {onlyInternal && customerLogs && customerLogs.length > 0 && renderCustomerLogMarkers && (
          <div className="w-full flex flex-col gap-4 py-2">
            {renderCustomerLogMarkers(customerLogs)}
          </div>
        )}
        {onlyInternal && renderTimelineMarkers && (
          <div className="flex w-full flex-col gap-4 py-2">{renderTimelineMarkers()}</div>
        )}
      </div>
    )
  }

  if (parts && parts.length > 0) {
    return parts.map((part, index) => {
      if (part.type === "text") {
        return (
          <div
            className={cn("flex flex-col", isUser ? "items-end" : "items-start")}
            key={`text-${index}`}
          >
            <div
              className={cn(
                chatBubbleVariants({
                  isUser,
                  isAuthor: !!authorName,
                  animation,
                }),
              )}
              data-fake={isFakeCustomer}
            >
              <MessageTopBadges badges={authorName ? [{ text: authorName }] : []} />
              {renderMedia()}
              {renderTranscription()}
              <MarkdownRenderer>{part.text}</MarkdownRenderer>
              {onlyInternal && actions ? (
                <div className="absolute -bottom-4 right-2 flex space-x-1 rounded-lg border bg-background p-1 text-foreground opacity-0 transition-opacity group-hover/message:opacity-100">
                  {actions}
                </div>
              ) : null}
            </div>
            {showTimeStamp && createdAt ? (
              <time
                dateTime={createdAt.toISOString()}
                className={cn(
                  "mt-1 block px-1 text-xs opacity-50",
                  animation !== "none" && "duration-500 animate-in fade-in-0",
                )}
              >
                {formattedTime}
              </time>
            ) : null}
          </div>
        )
      } else if (part.type === "reasoning") {
        return <ReasoningBlock key={`reasoning-${index}`} part={part} />
      } else if (part.type === "tool-invocation") {
        return <ToolCall key={`tool-${index}`} toolInvocations={[part.toolInvocation]} />
      }
      return null
    })
  }

  if (toolInvocations && toolInvocations.length > 0) {
    return <ToolCall toolInvocations={toolInvocations} />
  }

  return (
    <div
      className={cn(
        "flex flex-col",
        isUser ? "items-end" : "items-start",
        onClick && "cursor-pointer",
      )}
      onClick={
        onClick
          ? (e) => {
              e.stopPropagation()
              onClick()
            }
          : undefined
      }
    >
      <div
        className={cn(
          chatBubbleVariants({ isUser, isAuthor: isExternalAssistantMessage, animation }),
        )}
        data-fake={isFakeCustomer}
        style={colorConfig?.assistant}
        id={`message-${id}`}
        tabIndex={0}
      >
        <MessageTopBadges badges={topBadges} />
        {renderMedia()}
        {renderTranscription()}
        {renderWhatsappTemplate()}
        {content ? <MarkdownRenderer>{content}</MarkdownRenderer> : null}
        {renderWhatsappTemplateButtons()}
        {renderAttachments && renderAttachments()}
        {onlyInternal && actions ? (
          <div className="absolute -bottom-4 right-2 flex space-x-1 rounded-lg border bg-background p-1 text-foreground opacity-0 transition-opacity group-hover/message:opacity-100">
            {actions}
          </div>
        ) : null}
      </div>
      {messageReaction && (
        <div className="py-2 px-3 text-sm bg-muted rounded-full border border-background -translate-y-2 ml-2">
          <span>{messageReaction}</span>
        </div>
      )}
      {failedAt && failedReason && (
        <Tooltip>
          <TooltipTrigger className="z-10 ml-2 -translate-y-2 cursor-default" asChild>
            <span className="inline-flex rounded-lg border bg-background p-1">
              <AlertTriangle className="size-4 text-destructive" />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <span>{failedReason}</span>
          </TooltipContent>
        </Tooltip>
      )}
      {(showTimeStamp && createdAt) || flagCounts ? (
        <div
          className={cn(
            "mt-1 flex w-full items-center justify-start gap-2 px-1 text-xs",
            animation !== "none" && "duration-500 animate-in fade-in-0",
          )}
        >
          {showTimeStamp && createdAt ? (
            <time dateTime={createdAt.toISOString()} className="flex items-center gap-1 opacity-50">
              {formattedTime}
              {onlyInternal && readAt && <CheckCheck size={16} />}
            </time>
          ) : null}
          {flagCounts ? (
            <FlagsBadge issuesCount={flagCounts.issues} frictionsCount={flagCounts.frictions} />
          ) : null}
        </div>
      ) : null}
      {onlyInternal && tickets && tickets.length > 0 && (
        <div className="w-full flex flex-col gap-4 py-2">
          {tickets.map((ticket, ticketIndex) => (
            <SupportMarker
              key={`${ticket.kind}-${ticket.id}-${ticketIndex}`}
              supportCopy={supportCopy}
              ticketId={ticket.id}
              kind={ticket.kind}
              solved={"resolvedAutomatically" in ticket && ticket.resolvedAutomatically}
              questionSubject={ticket.kind === "question" ? ticket.subject : undefined}
              questionSummary={ticket.kind === "question" ? ticket.summary : undefined}
            />
          ))}
        </div>
      )}
      {onlyInternal && opportunities && opportunities.length > 0 && (
        <div className="w-full flex flex-col gap-4 py-2">
          {opportunities.map((opportunity) => (
            <OpportunityMarker
              key={opportunity.id}
              opportunity={opportunity}
              opportunityCopy={opportunityCopy}
            />
          ))}
        </div>
      )}
      {onlyInternal &&
        capabilityExecutions &&
        capabilityExecutions.length > 0 &&
        renderCapabilityExecutions && (
          <div className="w-full flex flex-col gap-4 py-2">
            {renderCapabilityExecutions(capabilityExecutions)}
          </div>
        )}
      {onlyInternal && renderTimelineMarkers && (
        <div className="flex w-full flex-col gap-4 py-2">{renderTimelineMarkers()}</div>
      )}
    </div>
  )
}

function dataUrlToUint8Array(data: string) {
  const base64 = data.split(",")[1]
  const buf = Buffer.from(String(base64), "base64")
  return new Uint8Array(buf)
}

const ReasoningBlock = ({ part }: { part: ReasoningPart }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="mb-2 flex flex-col items-start">
      <Collapsible
        open={isOpen}
        onOpenChange={setIsOpen}
        className="group w-full overflow-hidden rounded-lg border bg-muted/50"
      >
        <div className="flex items-center p-2">
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]:rotate-90" />
              <span>Thinking</span>
            </button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent forceMount>
          <motion.div
            initial={false}
            animate={isOpen ? "open" : "closed"}
            variants={{
              open: { height: "auto", opacity: 1 },
              closed: { height: 0, opacity: 0 },
            }}
            transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
            className="border-t"
          >
            <div className="p-2">
              <div className="whitespace-pre-wrap text-xs">{part.reasoning}</div>
            </div>
          </motion.div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

function ToolCall({ toolInvocations }: Pick<ChatMessageProps, "toolInvocations">) {
  if (!toolInvocations?.length) return null

  return (
    <div className="flex flex-col items-start gap-2">
      {toolInvocations.map((invocation, index) => {
        const isCancelled = invocation.state === "result" && invocation.result.__cancelled === true

        if (isCancelled) {
          return (
            <div
              key={index}
              className="flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
            >
              <Ban className="h-4 w-4" />
              <span>
                Cancelled{" "}
                <span className="font-mono">
                  {"`"}
                  {invocation.toolName}
                  {"`"}
                </span>
              </span>
            </div>
          )
        }

        switch (invocation.state) {
          case "partial-call":
          case "call":
            return (
              <div
                key={index}
                className="flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
              >
                <Terminal className="h-4 w-4" />
                <span>
                  Calling{" "}
                  <span className="font-mono">
                    {"`"}
                    {invocation.toolName}
                    {"`"}
                  </span>
                  ...
                </span>
                <Loader2 className="h-3 w-3 animate-spin" />
              </div>
            )
          case "result":
            return (
              <div
                key={index}
                className="flex flex-col gap-1.5 rounded-lg border bg-muted/50 px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Code2 className="h-4 w-4" />
                  <span>
                    Result from{" "}
                    <span className="font-mono">
                      {"`"}
                      {invocation.toolName}
                      {"`"}
                    </span>
                  </span>
                </div>
                <pre className="overflow-x-auto whitespace-pre-wrap text-foreground">
                  {JSON.stringify(invocation.result, null, 2)}
                </pre>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}

function SupportMarker({
  ticketId,
  solved,
  supportCopy,
  kind,
  questionSubject,
  questionSummary,
}: {
  ticketId: string
  solved?: boolean
  supportCopy: ChatMessageProps["supportCopy"]
  kind: Ticket["kind"]
  questionSubject?: string | null
  questionSummary?: string
}) {
  function getText() {
    if (kind === "ticket") return supportCopy?.ticketCreated
    if (solved) return supportCopy?.questionSolvedAutomatically
    return supportCopy?.questionCreated
  }

  let icon = <MessageCircleQuestion size={16} />
  if (kind === "ticket") icon = <Headset size={16} />
  if (solved) icon = <Sparkles size={16} />

  const subjectLabel = supportCopy?.questionSubjectLabel
  const summaryLabel = supportCopy?.questionSummaryLabel
  const hasQuestionPopover =
    kind === "question" && !!subjectLabel && !!summaryLabel && questionSummary !== undefined

  if (hasQuestionPopover) {
    return (
      <div
        className="w-full flex items-center justify-center text-support dark:text-support-secondary text-xs font-medium relative group"
        id={`support-${kind}-${ticketId}`}
      >
        <div className="border border-b border-support/20 dark:border-support-secondary/20 absolute w-full" />
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex gap-2 px-4 bg-background z-10 relative group-[.active-group]:bg-support dark:group-[.active-group]:bg-support-secondary group-[.active-group]:text-primary-foreground duration-150 group-[.active-group]:ring-10 rounded-full ring-support dark:ring-support-secondary cursor-pointer hover:text-opacity-80"
            >
              {icon}
              <span>{getText()}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="flex flex-col gap-3 max-w-md">
            <div className="flex items-center gap-2 text-support dark:text-support-secondary">
              <span className="shrink-0">{icon}</span>
              <span className="text-sm font-medium">{getText()}</span>
            </div>
            {questionSubject?.trim() ? (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">{subjectLabel}</p>
                <p className="text-base text-muted-foreground whitespace-pre-wrap">
                  {questionSubject}
                </p>
              </div>
            ) : null}
            {questionSummary?.trim() ? (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium">{summaryLabel}</p>
                <p className="text-base text-muted-foreground whitespace-pre-wrap">
                  {questionSummary}
                </p>
              </div>
            ) : null}
          </PopoverContent>
        </Popover>
      </div>
    )
  }

  return (
    <div
      className="w-full flex items-center justify-center text-support dark:text-support-secondary text-xs font-medium relative group"
      id={`support-${kind}-${ticketId}`}
    >
      <div className="border border-b border-support/20 dark:border-support-secondary/20 absolute w-full" />
      <div className="flex gap-2 px-4 bg-background z-10 relative group-[.active-group]:bg-support dark:group-[.active-group]:bg-support-secondary group-[.active-group]:text-primary-foreground duration-150 group-[.active-group]:ring-10 rounded-full ring-support dark:ring-support-secondary">
        {icon}
        {getText()}
      </div>
    </div>
  )
}

function OpportunityMarker({
  opportunity,
  opportunityCopy,
}: {
  opportunity: Opportunity
  opportunityCopy?: string
}) {
  const icon = <Award size={16} />
  const copy = opportunityCopy ?? "Business opportunity detected"

  return (
    <div
      className="w-full flex items-center justify-center text-chart-1 dark:text-chart-2 text-xs font-medium relative group"
      id={`opportunity-${opportunity.id}`}
    >
      <div className="border border-b border-chart-1/20 dark:border-chart-2/20 absolute w-full" />
      <div className="flex gap-2 px-4 bg-background z-10 relative group-[.active-group]:bg-support dark:group-[.active-group]:bg-support-secondary group-[.active-group]:text-primary-foreground duration-150 group-[.active-group]:ring-10 rounded-full ring-support dark:ring-support-secondary">
        <div className="flex items-center gap-2">
          {icon}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs">{copy}</span>
            </TooltipTrigger>
            <TooltipContent>
              <span className="text-xs">{opportunity.summary}</span>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

function MessageTopBadges({ badges }: { badges: Array<{ text: string; tooltip?: string }> }) {
  if (!badges.length) return null

  return (
    <>
      {badges.map(({ text, tooltip }, index) => {
        const badge = (
          <div
            key={index}
            tabIndex={tooltip ? 0 : undefined}
            className="bg-muted w-fit px-2 py-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="text-xs break-words text-muted-foreground">{text}</span>
          </div>
        )

        if (!tooltip) return badge

        return (
          <Tooltip key={index}>
            <TooltipTrigger asChild>{badge}</TooltipTrigger>
            <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
          </Tooltip>
        )
      })}
    </>
  )
}
